import type { ISidebarLink } from "@/types/(company)/roles.interface";

export type SidebarSpace = "myspace" | "company" | "admin";

const sectionOrderOf = (link: ISidebarLink, space: SidebarSpace) => {
  if (space === "myspace") return link.mySpaceOrder;
  if (space === "company") return link.companyOrder;
  return link.adminOrder;
};

const sectionNameOf = (link: ISidebarLink, space: SidebarSpace) => {
  if (space === "myspace") return link.mySpaceName;
  if (space === "company") return link.companyName;
  return link.adminName;
};

export const sortSidebarLinks = (
  links: ISidebarLink[],
  space: SidebarSpace,
) =>
  [...links].sort((a, b) => {
    const aSection = sectionOrderOf(a, space);
    const bSection = sectionOrderOf(b, space);
    // Items with a section-specific order always come before items without one.
    if (aSection != null && bSection == null) return -1;
    if (aSection == null && bSection != null) return 1;
    const aKey = aSection ?? a.order;
    const bKey = bSection ?? b.order;
    if (aKey !== bKey) return aKey - bKey;
    return a.name.localeCompare(b.name);
  });

export const getSidebarDisplayName = (
  link: ISidebarLink,
  space: SidebarSpace,
) => sectionNameOf(link, space) ?? link.name;

export interface SidebarCategory {
  id: number;
  name: string;
  order: number;
}

export interface SidebarGroup<T> {
  id: number | null;
  name: string;
  items: T[];
}

/**
 * Groups already-sorted sidebar items by their `categoryId` into ordered groups
 * (mirroring the admin sidebar's grouped nav). Group order follows the
 * category `order`; items with no/unknown category collapse into a trailing
 * "Other" group. Item order within each group is preserved.
 */
export const groupByCategory = <T extends { categoryId: number | null }>(
  items: T[],
  categories: SidebarCategory[],
  /** Label for items with no category — passed in already translated. */
  uncategorizedLabel = "Other",
): SidebarGroup<T>[] => {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const groups = new Map<number | null, SidebarGroup<T>>();

  for (const item of items) {
    const cat =
      item.categoryId != null ? byId.get(item.categoryId) : undefined;
    const key = cat ? cat.id : null;
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        name: cat ? cat.name : uncategorizedLabel,
        items: [],
      });
    }
    groups.get(key)!.items.push(item);
  }

  return [...groups.values()].sort((a, b) => {
    if (a.id == null) return 1; // uncategorized last
    if (b.id == null) return -1;
    return (byId.get(a.id)?.order ?? 0) - (byId.get(b.id)?.order ?? 0);
  });
};
